<template>
  <div
    class="modal fit-content-modal auth-required-modal"
    :class="{ 'is-active': modelValue }"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('distribute-landing.auth-modal.title')"
    data-test="auth-required-modal"
  >
    <div
      class="modal-background"
      @click="closeModal"
    />
    <div class="modal-card auth-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <span class="icon"><i class="fas fa-rocket mr-2" /></span>
          {{ $t('distribute-landing.auth-modal.title') }}
        </p>
        <button
          class="modal-close-button"
          aria-label="close"
          data-test="auth-modal-close"
          @click="closeModal"
        >
          <i class="fas fa-times" />
        </button>
      </header>
      <section class="modal-card-body">
        <div
          class="auth-tabs"
          role="tablist"
        >
          <button
            type="button"
            class="auth-tab"
            :class="{ 'is-active': mode === 'signup' }"
            role="tab"
            :aria-selected="mode === 'signup'"
            data-test="auth-modal-signup-tab"
            @click="setMode('signup')"
          >
            {{ $t('auth.create-account') }}
          </button>
          <button
            type="button"
            class="auth-tab"
            :class="{ 'is-active': mode === 'login' }"
            role="tab"
            :aria-selected="mode === 'login'"
            data-test="auth-modal-login-tab"
            @click="setMode('login')"
          >
            {{ $t('auth.sign-in') }}
          </button>
        </div>

        <div class="social-row">
          <button
            type="button"
            class="social-button"
            :disabled="isSubmitting"
            data-test="auth-modal-google"
            @click="handleGoogle"
          >
            <i class="fab fa-google" />
            <span>Google</span>
          </button>
          <button
            type="button"
            class="social-button"
            :disabled="isSubmitting"
            data-test="auth-modal-apple"
            @click="handleApple"
          >
            <i class="fab fa-apple" />
            <span>Apple</span>
          </button>
        </div>

        <div class="or-divider">
          <span>{{ $t('auth.or') }}</span>
        </div>

        <form
          v-if="mode === 'signup'"
          novalidate
          data-test="auth-modal-signup-form"
          @submit.prevent="handleSignup"
        >
          <div class="form-grid">
            <BaseInput
              v-model="signup.firstName"
              :label="$t('auth.first-name')"
              :placeholder="$t('auth.first-name')"
              :has-error="!!errors.firstName"
              :error-message="errors.firstName"
              left-icon="fas fa-user"
              required
              data-test="auth-modal-first-name"
              @blur="validate('firstName')"
            />
            <BaseInput
              v-model="signup.lastName"
              :label="$t('auth.last-name')"
              :placeholder="$t('auth.last-name')"
              :has-error="!!errors.lastName"
              :error-message="errors.lastName"
              left-icon="fas fa-user"
              required
              data-test="auth-modal-last-name"
              @blur="validate('lastName')"
            />
            <BaseInput
              v-model="signup.email"
              type="email"
              :label="$t('auth.email')"
              :placeholder="$t('auth.email')"
              :has-error="!!errors.email"
              :error-message="errors.email"
              left-icon="fas fa-envelope"
              autocomplete="email"
              class="is-full"
              required
              data-test="auth-modal-email"
              @blur="validate('email')"
            />
            <BaseInput
              v-model="signup.password"
              type="password"
              :label="$t('auth.password')"
              :placeholder="$t('auth.password')"
              :has-error="!!errors.password"
              :error-message="errors.password"
              left-icon="fas fa-lock"
              autocomplete="new-password"
              class="is-full"
              required
              data-test="auth-modal-password"
              @blur="validate('password')"
            />
          </div>

          <div class="terms-row">
            <label class="checkbox">
              <input
                v-model="signup.terms"
                type="checkbox"
                data-test="auth-modal-terms"
              >
              <span>
                {{ $t('auth.agree-to-terms') }}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ $t('auth.terms-and-conditions') }}</a>
              </span>
            </label>
            <p
              v-if="errors.terms"
              class="help is-danger"
              data-test="auth-modal-terms-error"
            >
              {{ errors.terms }}
            </p>
          </div>

          <div
            v-if="errors.global"
            class="error-banner"
            data-test="auth-modal-global-error"
          >
            <i class="fas fa-exclamation-circle" /> {{ errors.global }}
          </div>

          <button
            type="submit"
            class="submit-button"
            :disabled="isSubmitting"
            data-test="auth-modal-signup-submit"
          >
            <span v-if="!isSubmitting">
              <i class="fas fa-rocket mr-2" />
              {{ $t('distribute-landing.auth-modal.signup-submit') }}
            </span>
            <span v-else>
              <i class="fas fa-spinner fa-spin mr-2" />
              {{ $t('distribute-landing.progress-account') }}
            </span>
          </button>
        </form>

        <form
          v-else
          novalidate
          data-test="auth-modal-login-form"
          @submit.prevent="handleLogin"
        >
          <div class="form-grid">
            <BaseInput
              v-model="login.email"
              type="email"
              :label="$t('auth.email')"
              :placeholder="$t('auth.email')"
              :has-error="!!errors.loginEmail"
              :error-message="errors.loginEmail"
              left-icon="fas fa-envelope"
              autocomplete="email"
              class="is-full"
              required
              data-test="auth-modal-login-email"
              @blur="validate('loginEmail')"
            />
            <BaseInput
              v-model="login.password"
              type="password"
              :label="$t('auth.password')"
              :placeholder="$t('auth.password')"
              :has-error="!!errors.loginPassword"
              :error-message="errors.loginPassword"
              left-icon="fas fa-lock"
              autocomplete="current-password"
              class="is-full"
              required
              data-test="auth-modal-login-password"
              @blur="validate('loginPassword')"
            />
          </div>

          <div
            v-if="errors.global"
            class="error-banner"
            data-test="auth-modal-global-error"
          >
            <i class="fas fa-exclamation-circle" /> {{ errors.global }}
          </div>

          <button
            type="submit"
            class="submit-button"
            :disabled="isSubmitting"
            data-test="auth-modal-login-submit"
          >
            <span v-if="!isSubmitting">
              <i class="fas fa-sign-in-alt mr-2" />
              {{ $t('auth.sign-in') }}
            </span>
            <span v-else>
              <i class="fas fa-spinner fa-spin mr-2" />
              {{ $t('distribute-landing.progress-account') }}
            </span>
          </button>

          <p class="forgot-link">
            <router-link
              to="/auth/forgot-password"
              target="_blank"
            >
              {{ $t('auth.forgot-password') }}
            </router-link>
          </p>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseInput from '@/components/ui/BaseInput.vue';
import { useAuthStore } from '@/store/auth';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  defaultEmail: {
    type: String,
    default: '',
  },
  source: {
    type: String,
    default: 'distribute-landing',
  },
  utm: {
    type: Object,
    default: () => ({}),
  },
  initialMode: {
    type: String,
    default: 'signup',
    validator: (value) => ['signup', 'login'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'success', 'close']);

const { t } = useI18n();
const authStore = useAuthStore();

const mode = ref(props.initialMode);
const isSubmitting = ref(false);
const errors = ref({});

const signup = ref({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  terms: false,
});

const login = ref({
  email: '',
  password: '',
});

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      errors.value = {};
      mode.value = props.initialMode;
      if (props.defaultEmail) {
        signup.value.email = props.defaultEmail;
        login.value.email = props.defaultEmail;
      }
    }
  },
);

watch(
  () => props.initialMode,
  (next) => {
    if (props.modelValue) {
      mode.value = next;
    }
  },
);

const setMode = (next) => {
  if (isSubmitting.value) return;
  mode.value = next;
  errors.value = {};
};

const closeModal = () => {
  if (isSubmitting.value) return;
  emit('update:modelValue', false);
  emit('close');
};

const validate = (field) => {
  switch (field) {
  case 'firstName':
    errors.value.firstName = signup.value.firstName.trim()
      ? ''
      : t('distribute-landing.error-required');
    break;
  case 'lastName':
    errors.value.lastName = signup.value.lastName.trim()
      ? ''
      : t('distribute-landing.error-required');
    break;
  case 'email':
    errors.value.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signup.value.email)
      ? ''
      : t('distribute-landing.error-email');
    break;
  case 'password':
    errors.value.password = signup.value.password.length >= 8
      ? ''
      : t('distribute-landing.error-password');
    break;
  case 'loginEmail':
    errors.value.loginEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(login.value.email)
      ? ''
      : t('distribute-landing.error-email');
    break;
  case 'loginPassword':
    errors.value.loginPassword = login.value.password
      ? ''
      : t('distribute-landing.error-required');
    break;
  }
};

const validateSignup = () => {
  errors.value = {};
  validate('firstName');
  validate('lastName');
  validate('email');
  validate('password');
  if (!signup.value.terms) {
    errors.value.terms = t('distribute-landing.error-terms');
  }
  return Object.values(errors.value).every((v) => !v);
};

const validateLogin = () => {
  errors.value = {};
  validate('loginEmail');
  validate('loginPassword');
  return Object.values(errors.value).every((v) => !v);
};

const emitSuccess = () => {
  emit('success');
  emit('update:modelValue', false);
};

// Hardcoded English fallbacks set by authStore actions when no backend message is returned.
// Treat them as "no real message" so we can show a translated fallback instead.
const STORE_ERROR_FALLBACKS = ['Registration failed', 'Authentication failed'];

const isStoreFallbackMessage = (msg) => !msg || STORE_ERROR_FALLBACKS.includes(msg);

// Mirrors LoginView.vue: backend returns an error code (e.g. invalid_password)
// that maps to login.<code> translation; if not a known key, show the message as-is.
const translateLoginError = (code) => {
  if (!code) return t('distribute-landing.error-generic');
  const key = `login.${code}`;
  const translated = t(key);
  return translated !== key ? translated : code;
};

const handleSignup = async () => {
  if (!validateSignup()) return;

  isSubmitting.value = true;
  errors.value.global = '';

  try {
    const response = await authStore.register({
      email: signup.value.email.trim(),
      username: signup.value.email.trim(),
      password: signup.value.password,
      name: signup.value.firstName.trim(),
      last_name: signup.value.lastName.trim(),
      source: props.source,
      ...props.utm,
    });

    if (response.success) {
      emitSuccess();
    } else {
      errors.value.global = isStoreFallbackMessage(response.error)
        ? t('distribute-landing.error-register')
        : response.error;
    }
  } catch (e) {
    errors.value.global = e?.response?.data?.message
      || e.message
      || t('distribute-landing.error-register');
  } finally {
    isSubmitting.value = false;
  }
};

const handleLogin = async () => {
  if (!validateLogin()) return;

  isSubmitting.value = true;
  errors.value.global = '';

  try {
    const response = await authStore.login(
      login.value.email.trim(),
      login.value.password,
    );

    if (response.success) {
      emitSuccess();
    } else if (response.requiresTwoFactor) {
      errors.value.global = t('distribute-landing.auth-modal.two-factor-required');
    } else {
      errors.value.global = isStoreFallbackMessage(response.error)
        ? t('distribute-landing.error-generic')
        : translateLoginError(response.error);
    }
  } catch (e) {
    errors.value.global = e?.response?.data?.message
      || e.message
      || t('distribute-landing.error-generic');
  } finally {
    isSubmitting.value = false;
  }
};

const handleGoogle = async () => {
  isSubmitting.value = true;
  errors.value.global = '';
  try {
    const response = await authStore.loginWithGoogle();
    if (response.success) {
      emitSuccess();
    } else if (response.error) {
      errors.value.global = response.error;
    }
  } catch (e) {
    errors.value.global = e?.response?.data?.message || e.message || t('distribute-landing.error-generic');
  } finally {
    isSubmitting.value = false;
  }
};

const handleApple = async () => {
  isSubmitting.value = true;
  errors.value.global = '';
  try {
    const response = await authStore.loginWithApple();
    if (response.success) {
      emitSuccess();
    } else if (response.error) {
      errors.value.global = response.error;
    }
  } catch (e) {
    errors.value.global = e?.response?.data?.message || e.message || t('distribute-landing.error-generic');
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ setMode });
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.auth-required-modal {
  &.modal {
    z-index: 50;
  }
}

.auth-modal-card {
  width: 100%;
  max-width: 520px;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.25);

  .body--dark & {
    background: #1a1a1a;
  }

  @media (max-width: $mobile) {
    max-width: 95vw;
    border-radius: 14px;
  }
}

.modal-card-head {
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  border-bottom: none;
  padding: 1.25rem 1.5rem;

  .modal-card-title {
    color: #ffffff !important;
    background: none !important;
    -webkit-text-fill-color: #ffffff !important;
    -webkit-background-clip: initial !important;
    background-clip: initial !important;
    font-weight: 700;
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

    .icon, i {
      color: #ffffff;
    }
  }

  .modal-close-button {
    background-color: rgba(255, 255, 255, 0.2);
    border: none;
    color: #ffffff;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
      transform: rotate(90deg);
    }
  }
}

.modal-card-body {
  padding: 1.75rem 1.75rem 2rem;
  background: var(--color-background, #ffffff);

  .body--dark & {
    background: #1a1a1a;
    color: #fff;
  }

  @media (max-width: $mobile) {
    padding: 1.25rem;
  }

  // Prevent iOS auto-zoom on focus by ensuring inputs are at least 16px
  :deep(.input),
  :deep(select) {
    font-size: 16px;
  }
}

.auth-tabs {
  display: flex;
  background: rgba($indiefy-blue, 0.06);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 1.25rem;

  .body--dark & {
    background: rgba(255, 255, 255, 0.06);
  }
}

.auth-tab {
  flex: 1;
  padding: 0.6rem 1rem;
  border: none;
  background: transparent;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--color-text-light, #4a5568);
  cursor: pointer;
  transition: all 0.2s ease;

  &.is-active {
    background: #ffffff;
    color: $indiefy-blue;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

    .body--dark & {
      background: #2a2a2a;
      color: #fff;
    }
  }
}

.social-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.social-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  min-height: 48px;
  background: #ffffff;
  border: 1px solid var(--color-border, rgba(0, 0, 0, 0.15));
  border-radius: 10px;
  font-weight: 600;
  color: var(--color-text, #1a202c);
  cursor: pointer;
  transition: all 0.2s;

  .body--dark & {
    background: #2a2a2a;
    color: #fff;
    border-color: rgba(255, 255, 255, 0.15);
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: $indiefy-blue;
    box-shadow: 0 4px 12px rgba($indiefy-blue, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i {
    font-size: 1.1rem;
  }
}

.or-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1rem 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border, rgba(0, 0, 0, 0.1));

    .body--dark & {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  span {
    padding: 0 1rem;
    color: var(--color-text-light, #4a5568);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  .is-full {
    grid-column: 1 / -1;
  }

  @media (max-width: $mobile) {
    grid-template-columns: 1fr;
  }
}

.terms-row {
  margin-top: 0.75rem;

  .checkbox {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    color: var(--color-text, #1a202c);
    font-size: 0.85rem;
    line-height: 1.5;

    .body--dark & {
      color: #fff;
    }

    input[type="checkbox"] {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin-top: 2px;
    }

    a {
      color: $indiefy-blue;
      text-decoration: underline;
    }
  }
}

.error-banner {
  background: rgba($danger, 0.1);
  color: $danger;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  margin-top: 0.85rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.submit-button {
  width: 100%;
  margin-top: 1.25rem;
  padding: 0.9rem 1.5rem;
  min-height: 52px;
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba($indiefy-pink, 0.3);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba($indiefy-pink, 0.4);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.85;
  }
}

.forgot-link {
  text-align: center;
  margin-top: 0.85rem;
  font-size: 0.85rem;

  a {
    color: $indiefy-blue;
    font-weight: 600;
  }
}
</style>
